import { Guest } from "@/types/Guest";
import type { Capability } from "@/types/Capability";
import { GuestType } from "@/types/enums/GuestType";
import { AcceptanceState } from "@/types/enums/AcceptanceState";
import { GuestState } from "@/types/enums/GuestState";
import {
  getCompanions,
  getGuestsByIds,
} from "@/lib/api/handlers/GuestHandling";

/**
 * A party — one main guest plus the companions that point at them — resolved
 * for the door: who is here, and who is already inside.
 *
 * Why this is a fetch and not a read off the row:
 *  - On the FLAT guest list the row's `companions[]` are chips, not people. The
 *    backend lookup projects `_id, firstName, lastName` and nothing else
 *    (GuestRepository.appendListEnrichmentStages), so there is no `checkedIn`
 *    to render a checkbox state from.
 *  - On the PARTY-GROUPED list they are full rows (GuestService.hydrateParties),
 *    so the same component would read two different shapes.
 *  - And door devices flip `checkedIn` underneath the admin between the page
 *    load and the click. The check-in-history bulk dialog refuses to pre-filter
 *    on a stale join for exactly that reason; here we simply re-read.
 */
export const PARTY_MAX = 25;

/**
 * Fields the party dialog needs. `checkedIn` decides the checkbox state,
 * `archived`/`acceptanceState`/`state` decide who may be offered at all (see
 * `isAdmittable` — the companions endpoint does NOT apply those gates).
 */
export const PARTY_FIELDS =
  "guestId,firstName,lastName,checkedIn,archived,acceptanceState,state,guestType,mainGuest";

export type PartyMember = {
  guestId: string;
  firstName?: string;
  lastName?: string;
  /**
   * Tri-state, as stored: `true` inside, `false` checked out again,
   * `null`/`undefined` never scanned. `undefined` ALSO means "this role may not
   * read the field" — a CUSTOM field allowlist can drop `checkedIn` from the
   * projection (GuestFieldMasker.projectFields), and "unknown" is then the
   * honest answer, not an error.
   */
  checkedIn?: boolean | null;
  isMainGuest: boolean;
};

export type Party = {
  members: PartyMember[];
  /**
   * Companions the server returned but this dialog will not offer: archived,
   * declined, or still an undecided request. Shown as a count so the number of
   * rows in the dialog cannot silently disagree with the chips in the guest
   * list without saying why.
   */
  hiddenCount: number;
};

/**
 * Which guest's party is this row part of?
 *
 * A companion row names its parent either as the bare `mainGuest` id or as the
 * hydrated `mainGuestDTO` — the guest list fills the first, the requests lane
 * the second — so both are accepted, the same way the association cell does it.
 * Returns undefined for a companion whose parent is not on the row at all
 * (a grandfathered `companion → companion` chain, or a parent outside a
 * row-scoped role): there is no party this dialog can honestly show.
 */
export function partyHostId(row: Guest): string | undefined {
  if (row.guestType === GuestType.COMPANION) {
    return row.mainGuest || row.mainGuestDTO?.guestId || undefined;
  }
  return row.guestId || undefined;
}

/**
 * Does this row have anyone else to check in with?
 *
 * Read off the row on purpose — it decides whether a CLICK opens a dialog at
 * all, and must not cost a request. A main guest counts its companions (party
 * mode overwrites `numCompanions` with the hydrated party size, the flat list
 * derives it from the same lookup that fills the chips); a companion always
 * belongs to a party as long as its parent is nameable.
 */
export function hasParty(row: Guest): boolean {
  if (row.guestType === GuestType.COMPANION) return !!partyHostId(row);
  const known = row.companions?.length ?? row.numCompanions ?? 0;
  return known > 0;
}

/**
 * The capabilities the party sheet cannot open without — BOTH of them.
 *
 * `loadParty` makes two requests, and they are gated differently:
 *   `getGuestsByIds` → `POST /{eventId}/guests-by-id` → `guests:read`
 *                      (`GuestResource.java:3291`)
 *   `getCompanions`  → `GET  /{eventId}/{guestId}/companions`
 *                      → `guests:read.companions` (`GuestResource.java:1815`)
 *
 * Gating on the second alone looked sufficient because the capability
 * catalogue marks `guests:read.companions` as implying `guests:read`
 * (`types/Capability.ts:135`) — but that `implies` list is FRONTEND metadata,
 * expanded when a role template is saved through the editor. A template
 * written straight through the API carries exactly the keys it was given, and
 * the backend has no implication table at all. So the gate has to name both.
 */
export const PARTY_SHEET_CAPABILITIES: readonly Capability[] = [
  "guests:read",
  "guests:read.companions",
];

/**
 * Should a click open the party sheet, or write straight through?
 *
 * Two questions, not one. `hasParty` answers "is there anybody else to bring
 * along"; this one also answers "may this caller SEE them". The sheet's first
 * act is `GET /{eventId}/{guestId}/companions`, gated on
 * `guests:read.companions` (`GuestResource.java:1815`) — a capability
 * `guests:check-in` does NOT imply, because there is no implication table at
 * all. A door role holding check-in but not companion-read reads
 * `numCompanions > 0` off its own row, opens the sheet, takes a 403 on load,
 * keeps `members` null and sits on a permanently disabled Confirm: the single
 * write it IS allowed to make becomes unreachable, where before this feature it
 * simply worked.
 *
 * Lives here rather than in either component because there are TWO doors into
 * the sheet — the guest-list row and the detail drawer — and a rule that only
 * one of them learns is the failure this codebase keeps repeating.
 */
export function opensPartySheet(
  row: Guest,
  can: (capability: Capability) => boolean,
): boolean {
  return PARTY_SHEET_CAPABILITIES.every((c) => can(c)) && hasParty(row);
}

/**
 * Is this guest IN the party — the one question every surface that shows a
 * party has to answer, and the one the server has already answered:
 *
 * ```
 * GuestRepository.activeCompanionPredicate()
 *   archived == false AND (acceptanceState IN {ACCEPTED, SYSTEM} OR state == WAITLISTED)
 * ```
 *
 * That predicate is what `numCompanions` counts, what the guest list renders,
 * what the check-in app admits, and what `scope=PARTY` returns. This is its
 * ONE frontend mirror — it was two, and the second one (the companion row
 * classifier) had quietly dropped the `state == WAITLISTED` arm, so a
 * waitlisted companion the server counts as party rendered as an open request
 * with no way to remove it.
 *
 * Used here to decide who may be checked in from the party sheet, and by
 * `companionRowKind` to decide which verb a companion row offers. Same
 * question, so: same function.
 */
export function isPartyMember(guest: Guest): boolean {
  if (guest.archived === true) return false;
  if (guest.state === GuestState.WAITLISTED) return true;
  return (
    guest.acceptanceState === AcceptanceState.ACCEPTED ||
    guest.acceptanceState === AcceptanceState.SYSTEM
  );
}

/**
 * May this person be checked in from here?
 *
 * `GET /{eventId}/{guestId}/companions` is called WITHOUT a `scope` here, on
 * purpose: the un-scoped set (DECLINED filtered and nothing else) is what this
 * dialog needs, because `hiddenCount` below is the DIFFERENCE between what the
 * server returned and what may actually come through the door. Asking for
 * `COMPANION_SCOPE.PARTY` would let the server do the filtering and silently
 * drop that count to zero — the operator would stop being told that three
 * people in this party are not admissible.
 *
 * The un-scoped set returns archived and still-PENDING companions, and the
 * check-in write has no archived guard of its own
 * (`atomicSetCheckedIn` matches on `_id`, `eventId`, `checkedIn`). So the gate
 * has to happen here, and it is the same one the guest list itself applies:
 * not archived, and either an accepted/system row or one parked on the
 * waitlist.
 */
function isAdmittable(guest: Guest): boolean {
  return isPartyMember(guest);
}

/**
 * Who a SEAT can be planned for — the auto-seat roster's own line, mirrored.
 *
 * Not the door's rule. `SeatingGuestService.searchGuests` reads everybody who
 * is not archived and has not declined (`acceptanceState != DECLINED`), and
 * `planParty` then drops only those whose `state` says DECLINED — a still
 * PENDING request IS brought along to the table. The seat editor lists the
 * family so it can send each member's chair; a list built on the door's rule
 * left the pending companion out, and the server refused the whole write
 * because its family had one member more than the list.
 */
export function isSeatable(guest: Guest): boolean {
  if (guest.archived === true) return false;
  if (guest.acceptanceState === AcceptanceState.DECLINED) return false;
  if (guest.state === GuestState.DECLINED) return false;
  return true;
}

/**
 * Which question a party read answers: who may come through the door
 * (`admittable`, the default — the check-in sheet), or who gets a chair
 * (`seatable` — the seat editor). Same people most of the time; see
 * {@link isSeatable} for the day they are not.
 */
export type PartyRule = "admittable" | "seatable";

function toMember(guest: Guest, isMainGuest: boolean): PartyMember {
  return {
    guestId: guest.guestId ?? "",
    firstName: guest.firstName,
    lastName: guest.lastName,
    checkedIn: guest.checkedIn,
    isMainGuest,
  };
}

/**
 * The whole party of `hostId`, main guest first.
 *
 * Two requests, deliberately: the companions endpoint answers "who points at
 * this guest", it does not return the host. Both are read with the same
 * projection so the dialog renders one shape.
 *
 * Throws on a failed read — a dialog that opens on a half-loaded party would
 * offer to check in a subset while looking complete.
 */
/**
 * The few fields that decide whether a click opens the sheet at all.
 *
 * The guest list and the drawer read that off the row they already hold. The
 * check-in HISTORY cannot: a log row is a check-in EVENT, and the server joins
 * exactly four guest fields onto it — firstName, lastName, email and
 * checkInOutStatus (`CheckInLogRepository.guestLookupStage`). `numCompanions`
 * is filterable there but never projected, so the history doors have to ask.
 */
export const PARTY_PROBE_FIELDS =
  "guestId,firstName,lastName,checkedIn,numCompanions,guestType,mainGuest";

/**
 * Read just enough of a guest to answer "does this one open the party sheet?".
 *
 * One request, and the row it returns is also the `row` prop the sheet wants,
 * so nothing is fetched twice for the decision. The sheet still re-reads the
 * PARTY itself when it opens — that read is the fresh one, and it is the point.
 *
 * Returns null when the guest cannot be read (gone, or outside a row-scoped
 * role): the caller then behaves exactly as it did before the sheet existed.
 */
export async function loadPartyProbe(
  eventId: string,
  guestId: string,
): Promise<Guest | null> {
  const result = await getGuestsByIds(
    eventId,
    [guestId],
    "",
    [],
    PARTY_PROBE_FIELDS,
    0,
    1,
  );
  if (!Array.isArray(result)) return null;
  return result[0] ?? null;
}

export async function loadParty(
  eventId: string,
  hostId: string,
  rule: PartyRule = "admittable",
): Promise<Party> {
  const keep = rule === "seatable" ? isSeatable : isAdmittable;
  const [hostResult, companionResult] = await Promise.all([
    // `filters` sits BEFORE `fields` in the handler (the client swaps them), and
    // `pageSize` defaults to 10 — both are passed explicitly so a wide party
    // cannot be silently truncated by a default.
    getGuestsByIds(eventId, [hostId], "", [], PARTY_FIELDS, 0, 1),
    getCompanions(eventId, hostId, PARTY_FIELDS),
  ]);

  // Both handlers RETURN a `ServerError` instead of throwing, so a failed read
  // arrives as an object and would otherwise be iterated as an empty party.
  if (!Array.isArray(hostResult)) throw new Error(hostResult?.message || "unknown");
  if (!Array.isArray(companionResult)) {
    throw new Error(companionResult?.message || "unknown");
  }

  const host = hostResult[0];
  if (!host?.guestId) throw new Error("guestNotInEvent");

  const members: PartyMember[] = [];
  let hiddenCount = 0;
  // The door sheet always shows the host (the row the operator clicked, and
  // the person the party is named after). A chair list must not: an archived
  // or declined host is not on the seating roster, and naming them would make
  // the server refuse the family for "a guest outside this party".
  if (rule === "seatable" && !keep(host)) hiddenCount++;
  else members.push(toMember(host, true));

  for (const companion of companionResult) {
    if (!companion.guestId) continue;
    if (!keep(companion)) {
      hiddenCount++;
      continue;
    }
    members.push(toMember(companion, false));
  }

  return { members, hiddenCount };
}
